import { Button, Input, Label, confirm, toast } from "@devhop/ui";
import { useCallback, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Plus, Trash2 } from "lucide-react";
import type { WorkCalendarDayOffDto } from "../../domain/types";
import { useDeleteDayOff, useWorkCalendarDayOffs } from "../api/queries";
import { DayOffAddDialog } from "../ui/DayOffAddDialog";

export function CalendarSettingsPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const dayOffsQuery = useWorkCalendarDayOffs({
    year,
    start: `${year}-01-01`,
    end: `${year}-12-31`,
  });
  const deleteMutation = useDeleteDayOff();
  const dayOffs = dayOffsQuery.data ?? [];

  const handleDelete = useCallback(
    async (d: WorkCalendarDayOffDto) => {
      const ok = await confirm({
        title: "ລຶບວັນພັກ?",
        description: `${d.date}${d.description ? ` — ${d.description}` : ""} ຈະຖືກລຶບອອກຖາວອນ.`,
        actionText: "ລຶບ",
        ActionProps: { variant: "destructive" },
      });
      if (!ok) return;
      try {
        await deleteMutation.mutateAsync(d.id);
        toast.success("ລຶບວັນພັກແລ້ວ");
        dayOffsQuery.refetch();
      } catch {
        toast.error("ບໍ່ສາມາດລຶບໄດ້");
      }
    },
    [deleteMutation, dayOffsQuery],
  );

  return (
    <>
      <Header />
      <Main>
        {/* Toolbar: title + description (same pattern as ProjectListPage, AuditPlanListPage) */}
        <div
          className="mb-6 flex items-center justify-between"
          data-tourid="calendar-settings-toolbar"
        >
          <div>
            <h1 className="font-semibold text-2xl">ກຳນົດວັນພັກ</h1>
            <p className="text-muted-foreground text-sm">
              ກຳນົດວັນພັກປະຈຳປີ ແລະ ວັນພິເສດ (ວັນເຮັດວຽກພິເສດ)
            </p>
          </div>
          <Button
            variant="default"
            onClick={() => setAddDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            ເພີ່ມວັນພັກ / ວັນພິເສດ
          </Button>
        </div>

        {/* Card: filter row + content (same pattern as ProjectFilter + Table) */}
        <div className="flex flex-col rounded-xl border bg-card">
          <div className="flex flex-wrap items-end gap-4 border-b p-4">
            <div className="grid gap-2">
              <Label htmlFor="settings-year">ປີ</Label>
              <Input
                id="settings-year"
                type="number"
                className="w-24"
                value={year}
                onChange={(e) =>
                  setYear(Number(e.target.value) || currentYear)
                }
                min={currentYear - 2}
                max={currentYear + 2}
              />
            </div>
          </div>

          <div className="p-4">
            <h2 className="mb-4 font-medium text-lg">
              ວັນພັກປະຈຳປີ {year}
            </h2>
            {dayOffsQuery.isLoading ? (
              <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
            ) : dayOffs.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີວັນພັກໃນປີນີ້
              </p>
            ) : (
              <ul className="space-y-2">
                {dayOffs.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                  >
                    <span>
                      {d.date}
                      {d.description ? ` — ${d.description}` : ""}
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          d.type === "holiday"
                            ? "font-medium text-destructive"
                            : "text-muted-foreground"
                        }
                      >
                        {d.type === "holiday" ? "ວັນພັກ" : "ພິເສດ"}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(d)}
                        disabled={deleteMutation.isPending}
                        aria-label="ລຶບ"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Main>

      <DayOffAddDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        year={year}
        onSuccess={() => dayOffsQuery.refetch()}
      />
    </>
  );
}
